import React, { useContext, useCallback, useMemo } from 'react';

// DEPENDENCIES
import orderBy from 'lodash/orderBy';
import { useFormikContext } from 'formik';

// GLOBAL VARIABLES
import { RETAIL_WHOLESALE, WHOLESALE_FORMAT } from 'codes';
import { SALES_FILE_TYPE_IDS } from 'globals.js';

// HELPERS/FUNCTIONS
import { RecordContext } from 'helpers/getRecordData';

// GLOBAL COMPONENTS
import Form from 'core/form/Form';

// LOCAL COMPONENTS
import Card from '../parts/Card';
import Attachment from 'routers/interface/tools/attachment/Attachment';

// MAIN COMPONENT
const SaleAttachments = () => {
  const { refetchRecord } = useContext(RecordContext);
  const {
    values: {
      aryDealerAMFinance = [], //
      sale_type_category_code,
      inventory,
      wholesale_format_id
    } = {}
  } = useFormikContext() || {};
  const { new_used } = inventory || {};

  const productsSoldAttachments = useMemo(() => {
    const filtered = aryDealerAMFinance.reduce((arr, a, i) => {
      if (a.product_type === 0 && a.is_checked) {
        arr.push({
          ...a,
          index: i // used to match index for displaying existing attachments
        });
      }
      return arr;
    }, []);
    return orderBy(
      filtered || [],
      ['product_type', 'product_code', 'item_name'],
      ['asc', 'asc', 'asc']
    );
  }, [aryDealerAMFinance]);

  const isRetail = sale_type_category_code === RETAIL_WHOLESALE.retail.category_code;
  const retailWholesale =
    wholesale_format_id === WHOLESALE_FORMAT.detailed.numeric ? 'RETAIL' : sale_type_category_code; // treat Wholesale Detail same as Retail

  const edpDocumentType = {
    0: {
      RETAIL: SALES_FILE_TYPE_IDS.EDP_RETAIL_USED,
      WHOLESALE: SALES_FILE_TYPE_IDS.EDP_WHOLESALE_USED
    },
    1: {
      RETAIL: SALES_FILE_TYPE_IDS.EDP_RETAIL_NEW,
      WHOLESALE: SALES_FILE_TYPE_IDS.EDP_WHOLESALE_NEW
    }
  };

  const ebosDocumentType = {
    0: {
      RETAIL: SALES_FILE_TYPE_IDS.EBOS_RETAIL_USED,
      WHOLESALE: SALES_FILE_TYPE_IDS.EBOS_WHOLESALE_USED
    },
    1: {
      RETAIL: SALES_FILE_TYPE_IDS.EBOS_RETAIL_NEW,
      WHOLESALE: SALES_FILE_TYPE_IDS.EBOS_WHOLESALE_NEW
    }
  };

  const productsSoldDocumentType = {
    0: SALES_FILE_TYPE_IDS.AMFI_PRODUCT_RETAIL_USED,
    1: SALES_FILE_TYPE_IDS.AMFI_PRODUCT_RETAIL_NEW
  };

  const otherDocumentType = {
    0: {
      RETAIL: SALES_FILE_TYPE_IDS.OTHER_RETAIL_USED,
      WHOLESALE: SALES_FILE_TYPE_IDS.OTHER_WHOLESALE_USED
    },
    1: {
      RETAIL: SALES_FILE_TYPE_IDS.OTHER_RETAIL_NEW,
      WHOLESALE: SALES_FILE_TYPE_IDS.OTHER_WHOLESALE_NEW
    }
  };

  const edpTitle = isRetail ? 'Executed Deal Package (SA2)' : "Auction Invoice / WS Buyer's Order (SA2)";

  const handleSuccess = useCallback(() => {
    refetchRecord();
  }, [refetchRecord]);

  return (
    <Form.Body>
      <Attachment.Upload
        label={edpTitle}
        name="documentAttachment.edp"
        documentType={edpDocumentType?.[new_used]?.[retailWholesale]}
        uploadType="sales"
        onComplete={handleSuccess}
        downloadRecent
      />
      <Attachment.Upload
        label="Accounting Invoice / Bill of Sale (SA3)"
        name="documentAttachment.ebos"
        documentType={ebosDocumentType?.[new_used]?.[retailWholesale]}
        uploadType="sales"
        onComplete={handleSuccess}
        downloadRecent
      />
      {productsSoldAttachments.map(a => (
        <Attachment.Upload
          key={a.dealer_amfinance_id}
          label={`${a.item_name} - Executed Agreement`}
          name={`aryDealerAMFinance[${a.index}].aryAMFinanceAttachment[0]`}
          documentType={productsSoldDocumentType?.[new_used]}
          uploadType="sales"
          uploadParams={{
            dealerAMFinanceId: a.dealer_amfinance_id
          }}
          onComplete={handleSuccess}
          multiple={false}
        />
      ))}
      <Attachment.Upload
        label="Other"
        name="documentAttachment.other"
        documentType={otherDocumentType?.[new_used]?.[retailWholesale]}
        uploadType="sales"
        onComplete={handleSuccess}
      />
    </Form.Body>
  );
};

export default function SaleAttachment(props) {
  return <Card title="Sale Attachments" body={<SaleAttachments {...props} />} />;
}
